import { dealSummaryPdfApi } from "../../../api/client";
import i18n from "../../../i18n";

interface DealSummaryPdfEnvelope {
  bytes: string;
}

/**
 * Downloads the Deal Summary PDF from the BFF and opens it in a new tab.
 * Despite the `Content-Type: application/pdf` header, the BFF actually
 * returns a JSON envelope (`{ bytes: "<base64 PDF>" }`) rather than raw
 * bytes, so the arraybuffer is decoded as JSON and the `bytes` field is
 * base64-decoded before being wrapped in a Blob and opened via object URL.
 * @param {string} approvalId - The approval/deal ID.
 * @returns {Promise<void>}
 */
export async function downloadDealSummaryPdf(approvalId: string): Promise<void> {
  const pdfWindow = window.open("", "_blank");
  if (!pdfWindow) {
    throw new Error("Popup blocked");
  }

  pdfWindow.document.title = i18n.t("actions.dealSummaryTitle");

  const style = pdfWindow.document.createElement("style");
  style.textContent = `
    body {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100vh;
      margin: 0;
      font-family: sans-serif;
      color: #333;
    }
    .spinner {
      width: 24px;
      height: 24px;
      margin-right: 12px;
      border: 3px solid #ccc;
      border-top-color: #333;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;
  pdfWindow.document.head.append(style);

  const spinner = pdfWindow.document.createElement("div");
  spinner.className = "spinner";
  const loadingText = pdfWindow.document.createElement("span");
  loadingText.textContent = i18n.t("actions.dealSummaryLoading");
  pdfWindow.document.body.append(spinner, loadingText);

  try {
    const response = await dealSummaryPdfApi.getDealSummaryPdf(approvalId, {
      responseType: "arraybuffer",
    });

    const envelopeText = new TextDecoder().decode(response.data as unknown as ArrayBuffer);
    const { bytes } = JSON.parse(envelopeText) as DealSummaryPdfEnvelope;
    const pdfBytes = Uint8Array.from(atob(bytes), (char) => char.codePointAt(0)!);

    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);

    pdfWindow.location.href = blobUrl;

    pdfWindow.addEventListener("unload", () => {
      URL.revokeObjectURL(blobUrl);
    });
  } catch (error) {
    pdfWindow.document.body.replaceChildren();
    const errorText = pdfWindow.document.createElement("span");
    errorText.textContent = i18n.t("actions.dealSummaryError");
    pdfWindow.document.body.append(errorText);

    // eslint-disable-next-line no-console
    console.error("Error downloading Deal Summary PDF:", error);
    throw error;
  }
}
